import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import AuthCallback from './pages/AuthCallback';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ClientDashboard from './pages/ClientDashboard';
import StaffDashboard from './pages/StaffDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CaseDetail from './pages/CaseDetail';
import Messages from './pages/Messages';
import Documents from './pages/Documents';
import Calendar from './pages/Calendar';
import Analytics from './pages/Analytics';
import FeedbackForm from './pages/FeedbackForm';
import Resources from './pages/Resources';
import AdminResources from './pages/AdminResources';
import FirmSettings from './pages/FirmSettings';
import Announcements from './pages/Announcements';
import MyTasks from './pages/MyTasks';
import NotificationSettings from './pages/NotificationSettings';
import ReminderSettings from './pages/ReminderSettings';
import ChangePassword from './pages/ChangePassword';
import Help from './pages/Help';
import AdminFAQ from './pages/AdminFAQ';
import AdminGlossary from './pages/AdminGlossary';
import AdminSettings from './pages/AdminSettings';
import { API } from './config/api';
import './App.css';

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip auth check if user data was passed from login/callback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    
    checkAuth();
  }, [navigate, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCFE]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? React.cloneElement(children, { user }) : null;
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Synchronously check for session_id in URL fragment to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/cases/:caseId" element={
        <ProtectedRoute>
          <CaseDetail />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Documents />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Calendar />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Analytics />
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <FeedbackForm />
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute>
          <Resources />
        </ProtectedRoute>
      } />
      <Route path="/admin/resources" element={
        <ProtectedRoute>
          <AdminResources />
        </ProtectedRoute>
      } />
      <Route path="/admin/firm-settings" element={
        <ProtectedRoute>
          <FirmSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute>
          <Announcements />
        </ProtectedRoute>
      } />
      <Route path="/admin/faqs" element={
        <ProtectedRoute>
          <AdminFAQ />
        </ProtectedRoute>
      } />
      <Route path="/admin/glossary" element={
        <ProtectedRoute>
          <AdminGlossary />
        </ProtectedRoute>
      } />
      <Route path="/admin/reminder-settings" element={
        <ProtectedRoute>
          <ReminderSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/my-tasks" element={
        <ProtectedRoute>
          <MyTasks />
        </ProtectedRoute>
      } />
      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <NotificationSettings />
        </ProtectedRoute>
      } />
      {/* Redirect from email links to the correct notification settings path */}
      <Route path="/notification-settings" element={
        <ProtectedRoute>
          <NotificationSettings />
        </ProtectedRoute>
      } />
      <Route path="/settings/change-password" element={
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <Help />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function DashboardRouter({ user }) {
  if (!user) return null;

  if (user.role === 'client') {
    return <ClientDashboard user={user} />;
  } else if (user.role === 'staff') {
    return <StaffDashboard user={user} />;
  } else if (user.role === 'admin') {
    return <AdminDashboard user={user} />;
  }
  return <div>Unknown role</div>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;